import { z } from 'zod';

import { companySchema } from '../search/search-schema';

export enum EarningState {
  Beat = 'earning-beat',
  Miss = 'earning-miss',
  Meet = 'earning-meet',
}

export enum Expectation {
  Beat = 'beat',
  Miss = 'miss',
  Meet = 'meet',
}

export enum ScenarioEnum {
  Beat = 'beat',
  Miss = 'miss',
  Actual = 'actual',
}

export type HistoryResults = z.infer<typeof historyResultSchema>;
export const historyResultSchema = z.object({
  w1: z.number().nullable(),
  w2: z.number().nullable(),
  m1: z.number().nullable(),
  fiscal_year: z.number(),
  fiscal_period: z.string(),
  eps_surprise: z.number().nullable(),
  reporting_date: z.string().datetime(),
});

export type HistoryMeta = z.infer<typeof historyMetaSchema>;
export const historyMetaSchema = z.object({
  count: z.number(),
  medians: z.object({ w1: z.number(), w2: z.number(), m1: z.number() }),
});

export type History = z.infer<typeof historySchema>;
export const historySchema = z.object({
  meta: historyMetaSchema,
  results: z.array(historyResultSchema),
});

const scenarioDataSchema = z.object({
  period: z.enum(['w1', 'w2', 'm1']),
  low: z.number(),
  median: z.number(),
  high: z.number(),
});

export type Scenario = z.infer<typeof scenarioSchema>;

export const scenarioSchema = z
  .object({
    image_url: z.string(),
    data: z.array(scenarioDataSchema),
    history: historySchema.nullable(),
  })
  .nullable();

export type Earning = z.infer<typeof earningSchema>;

export const earningSchema = z.object({
  value: z.number().optional(),
  estimate: z.number().optional(),
  surprise: z.number().optional(),
  surprise_percentage: z.number().optional(),
});

export type AssetDetails = z.infer<typeof assetDetailsSchema>;

export const assetDetailsSchema = companySchema.extend({
  currency: z.string(),
  eps: earningSchema,
  facepalm: z.boolean(),
  logo_urls: z.object({
    svg_light: z.string(),
    svg_dark: z.string(),
    png_light: z.string(),
    png_dark: z.string(),
  }),
  revenue: earningSchema,
  scenarios: z.object({
    beat: scenarioSchema.optional(),
    miss: scenarioSchema.optional(),
    actual: scenarioSchema.optional(),
  }),
  ticker: z.string(),
});

export enum PrimaryStatus {
  OnlyEstimate = 'only_estimate',
  OnlyHistory = 'only_history',
  Both = 'both',
  None = 'none',
}

export enum SecondaryStatus {
  NotReported = 'not_reported',
  Analyzing = 'analyzing',
  Reported = 'reported',
}

export type Status = z.infer<typeof statusSchema>;
export const statusSchema = z.object({
  primary: z.nativeEnum(PrimaryStatus),
  secondary: z.nativeEnum(SecondaryStatus),
  tertiary: z.nativeEnum(EarningState).optional(),
});

export type AssetDetailsWithStatus = z.infer<
  typeof assetDetailsWithStatusSchema
>;
export const assetDetailsWithStatusSchema = assetDetailsSchema.extend({
  status: statusSchema,
});
